import { writable } from "svelte/store";
import { localUserStore } from "../Connection/LocalUserStore";

export interface MercadologicaPostIt {
    id: string;
    senderUuid: string;
    senderName: string;
    recipientUuid: string;
    recipientName: string;
    message: string;
    createdAt: number;
    readAt: number | null;
}

export interface MercadologicaPostItTarget {
    uuid: string;
    name: string;
}

interface MercadologicaPostItUiState {
    mode: "closed" | "compose" | "inbox";
    target?: MercadologicaPostItTarget;
    unreadCount: number;
}

function createMercadologicaPostItStore() {
    const { subscribe, update, set } = writable<MercadologicaPostItUiState>({
        mode: "closed",
        unreadCount: 0,
    });

    return {
        subscribe,

        openCompose(target: MercadologicaPostItTarget): void {
            update((state) => ({
                ...state,
                mode: "compose",
                target,
            }));
        },

        openInbox(): void {
            update((state) => ({
                ...state,
                mode: "inbox",
                target: undefined,
            }));
        },

        close(): void {
            update((state) => ({
                ...state,
                mode: "closed",
                target: undefined,
            }));
        },

        setUnreadCount(unreadCount: number): void {
            update((state) => ({
                ...state,
                unreadCount,
            }));
        },

        reset(): void {
            set({
                mode: "closed",
                unreadCount: 0,
            });
        },
    };
}

export const mercadologicaPostItStore = createMercadologicaPostItStore();

async function authenticatedRequest(path: string, options: RequestInit = {}): Promise<Response> {
    const token = localUserStore.getAuthToken();

    if (!token) {
        throw new Error("Sua sessão não está autenticada.");
    }

    const response = await fetch(path, {
        ...options,
        headers: {
            "Content-Type": "application/json",
            Authorization: token,
            ...options.headers,
        },
    });

    if (!response.ok) {
        let message = "Não foi possível concluir a operação.";

        try {
            const body = (await response.json()) as { error?: unknown };
            if (typeof body.error === "string") {
                message = body.error;
            }
        } catch {
            // Mantém a mensagem padrão.
        }

        throw new Error(message);
    }

    return response;
}

export interface MercadologicaPostItCollection {
    received: MercadologicaPostIt[];
    sent: MercadologicaPostIt[];
}

export async function fetchMercadologicaPostIts(): Promise<MercadologicaPostItCollection> {
    const response = await authenticatedRequest("/mercadologica/post-its");
    const result = (await response.json()) as {
        postIts: MercadologicaPostIt[];
        sentPostIts?: MercadologicaPostIt[];
        unreadCount: number;
    };

    mercadologicaPostItStore.setUnreadCount(result.unreadCount);

    return {
        received: result.postIts,
        sent: result.sentPostIts ?? [],
    };
}

export async function createMercadologicaPostIt(
    target: MercadologicaPostItTarget,
    message: string,
): Promise<MercadologicaPostIt> {
    const response = await authenticatedRequest("/mercadologica/post-its", {
        method: "POST",
        body: JSON.stringify({
            recipientUuid: target.uuid,
            recipientName: target.name,
            senderName: localUserStore.getName() ?? "Alguém",
            message,
        }),
    });

    return (await response.json()) as MercadologicaPostIt;
}

export async function markMercadologicaPostItAsRead(id: string): Promise<MercadologicaPostIt> {
    const response = await authenticatedRequest(`/mercadologica/post-its/${encodeURIComponent(id)}/read`, {
        method: "PATCH",
    });

    return (await response.json()) as MercadologicaPostIt;
}

export async function deleteMercadologicaPostIt(id: string): Promise<void> {
    await authenticatedRequest(`/mercadologica/post-its/${encodeURIComponent(id)}`, {
        method: "DELETE",
    });
}

export async function deleteMercadologicaSentPostIt(id: string): Promise<void> {
    await authenticatedRequest(`/mercadologica/post-its/sent/${encodeURIComponent(id)}`, {
        method: "DELETE",
    });
}
